<template>
  <div
    v-if="
      currentMission != null &&
      currentMission.typeName === 'ConstellationPerformanceAnalysisRequest'
    "
    style="overflow-x: hidden"
  >
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation></exo-mission-computation>
    </div>
    <div v-if="displayResult" m-0 p-0>
      <div v-if="loading">Loading data and map ...</div>
      <v-card v-if="!loading">
        <v-row align-center justify-center>
          <v-col cols="7">
            <span class="ml-4 headline grey--text"
              >{{ currentMission.name.toUpperCase() }} -
              {{
                getResultsFieldLabel(
                  appliedDashboardConfiguration.currentCategory
                )
              }}
              /
              {{
                getResultsFieldLabel(appliedDashboardConfiguration.currentMap)
              }}</span
            >
            <v-select
              class="ml-5"
              :items="missionsToCompare"
              v-model="otherMission"
              v-on:change="changeOtherMission()"
              :error="!dataLoaded"
              label="Other mission"
            >
            </v-select>
          </v-col>

          <v-spacer></v-spacer>
          <v-col cols="5" m-2 class="text-right">
            <v-dialog v-if="displayError" v-model="dialog" width="700">
              <template v-slot:activator="{ on: dialog }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      class="bug-button"
                      v-on="{ ...dialog, ...tooltip }"
                    >
                      <icon
                        class="white--text"
                        scale="1.5"
                        name="user-injured"
                      />
                    </v-btn>
                  </template>
                  <span>Bug report</span>
                </v-tooltip>
              </template>
              <error-log-display
                v-if="displayErrorCurrent"
                :root="_self"
                :computationLog="currentResultsLog"
              ></error-log-display>
              <error-log-display
                v-if="displayErrorOther"
                :root="_self"
                :computationLog="otherResultsLog"
              ></error-log-display>
              <error-log-display
                v-if="displayErrorConsistency"
                :root="_self"
                :errorMessage="errorConsistencyMessage"
              ></error-log-display>
            </v-dialog>
            <v-tooltip
              bottom
              class="caption justify-center warning--text"
              v-if="currentMission.dirty"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  class="white--text"
                  v-on="on"
                  @click.stop="requestComputation()"
                  color="warning"
                >
                  Start Recompute
                </v-btn>
              </template>
              <span
                >You have changed some components of the mission. This results
                does not match the current inputs. Please recompute the mission
                to update.</span
              >
            </v-tooltip>
            <v-bottom-sheet inset v-if="displayWarning">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        class="warning--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="emptyResult"
                >
                  Your constellation is blind during this period, you should
                  reconsider the configuration or select another result.
                </v-alert>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="this.warningOtherMission"
                >
                  {{ warningOtherMission }}
                </v-alert>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displaySpatialAverageWarning"
                >
                  <p>
                    The metrics were computed for a mesh with constant latitude
                    and longitude steps. This results in a greater concentration
                    of points at higher latitudes. The Statistics and the Global
                    histograms are computed based on the mesh points, which are
                    not uniformly distributed on the Earth surface.
                    Consequently, they artificially give more importance to the
                    results around higher latitudes.
                  </p>

                  <i>Nota Bene: </i> to obtain a mesh that represents the Earth
                  surface at higher latitudes, you may set the 'Mesh element
                  definition' to 'Constant area' in the Earth Mesh form.
                </v-alert>
              </v-card>
            </v-bottom-sheet>

            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click="openDashboardSettings"
                >
                  <icon scale="1.5" class="primary--text" name="cogs" />
                </v-btn>
              </template>
              <span>Dashboard settings</span>
            </v-tooltip>

            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="saveCurrentLayout()"
                >
                  <icon scale="1.5" class="primary--text" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="resetLayout()"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="undo-alt"
                  ></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row no-gutters class="ml-4" v-if="dataLoaded">
          <v-select
            class="mr-2"
            :items="mapCategories"
            v-model="editedDashboardConfiguration.currentCategory"
            label="Map Category"
            v-on:input="categoryUpdated"
          >
            <template slot="selection" slot-scope="data">
              {{ getResultsFieldLabel(data.item) }}
            </template>
            <template slot="item" slot-scope="data">
              {{ getResultsFieldLabel(data.item) }}
            </template>
          </v-select>
          <exo-help-dialog
            icon-scale="1.5"
            title="Collect time"
            v-if="
              editedDashboardConfiguration.currentCategory == 'pointCollectTime'
            "
          >
            <tooltipCollectTime />
          </exo-help-dialog>
          <v-select
            class="mr-2"
            :items="availableMapTypes"
            v-model="editedDashboardConfiguration.currentMap"
            label="Map type"
          >
            <template slot="selection" slot-scope="data">
              {{ getResultsFieldLabel(data.item) }}
            </template>
            <template slot="item" slot-scope="data">
              {{ getResultsFieldLabel(data.item) }}
            </template>
          </v-select>
          <exo-help-dialog icon-scale="1.5" title="Results Description">
            <tooltipRevisitGroundSegments />
          </exo-help-dialog>
          <v-card-actions class="mx-4 my-3">
            <v-btn @click="updateMap()" id="updateMapBtn" color="primary"
              >Update map</v-btn
            >
          </v-card-actions>
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentCategory ===
            'pointNumberOfAccesses'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The metric 'Number of accesses' does not support a global histogram,
            since it does not represent a list of instances through time, but
            rather one sole value for each point.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentMap ===
            'pointAccessDurationPercentile'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The access duration percentiles values are inverted. If the p-th
            percentile is equal to X, p% of the revisits on that point have a
            duration above or equal X.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentMap ===
            'pointSimultaneousSpacecraftsPercentile'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The 'number of spacecrafts simultaneously seen' percentiles values
            are inverted. If the p-th percentile is equal to X, then that point
            sees at least X spacecrafts on p% of the simulation duration.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          v-if="
            editedDashboardConfiguration.currentMap.includes('Percentile') ===
            true
          "
        >
          <v-col class="px-3" cols="5">
            <v-slider
              v-model="editedDashboardConfiguration.currentPercentileIndex"
              label="Percentile"
              color="rgb(51, 204, 255)"
              :tick-labels="commonPercentiles"
              :max="commonPercentiles.length - 1"
              ticks="always"
            >
            </v-slider>
          </v-col>
        </v-row>
      </v-card>

      <div v-if="buildingMap">Building map ...</div>
      <v-row no-gutters v-if="dataLoaded" justify="space-around" class="ma-2">
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.display3DView === true"
        >
          <v-card outlined>
            <v-card-title>{{
              appliedMetricName +
              (availableMeshOptionsCurrent.length > 1 ? ' on all meshes' : '')
            }}</v-card-title>
            <v-card-text>
              <cesium-viewer
                :currentData="currentDataRef"
                :resultsLog="currentResultsLog"
                :heatMapCommons="heatMapCommons"
                :displaySinglePointResults="displaySinglePointResults"
                :displayGroundStationTimelineFrom3DView="
                  displayGroundStationTimeline
                "
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.display3DView === true"
        >
          <v-card outlined>
            <v-card-title>{{
              appliedMetricName +
              (availableMeshOptionsOther.length > 1 ? ' on all meshes' : '')
            }}</v-card-title>
            <v-card-text>
              <cesium-viewer
                :currentData="currentDataOther"
                :resultsLog="otherResultsLog"
                :heatMapCommons="heatMapCommons"
                :displaySinglePointResults="displaySinglePointResults"
                :displayGroundStationTimelineFrom3DView="
                  displayGroundStationTimeline
                "
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHeatmapView === true"
        >
          <v-card outlined>
            <v-card-title>
              {{ appliedMetricName }}
              <v-spacer></v-spacer>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptionsCurrent"
                v-model="heatmapMeshIndexCurrent"
                label="Displayed mesh"
                v-if="availableMeshOptionsCurrent.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item, currentResultsLog.results)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item, currentResultsLog.results)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHeatmapViewRef"
              :ref="idWidgetHeatmapViewRef"
            ></div>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHeatmapView === true"
        >
          <v-card outlined>
            <v-card-title>
              {{ appliedMetricName }}
              <v-spacer></v-spacer>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptionsOther"
                v-model="heatmapMeshIndexOther"
                label="Displayed mesh"
                v-if="availableMeshOptionsOther.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item, otherResultsLog.results)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item, otherResultsLog.results)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHeatmapViewOther"
              :ref="idWidgetHeatmapViewOther"
            ></div>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHistogramView === true"
        >
          <v-card outlined>
            <v-card-title>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptionsCurrent.concat(allMeshesIndex)"
                v-model="statisticsHistogramMeshIndexCurrent"
                label="Displayed mesh"
                v-if="availableMeshOptionsCurrent.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item, currentResultsLog.results)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item, currentResultsLog.results)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHistogramViewRef"
              :ref="idWidgetHistogramViewRef"
            ></div>
            <v-card-actions>
              <v-row
                no-gutters
                class="px-6"
                v-if="displayHistogramOptions === true && dataReady === true"
              >
                <v-text-field
                  dense
                  class="px-2"
                  v-model="editedDashboardConfiguration.histogramBinSizeRef"
                  :label="
                    'Size of bins in x axis' +
                    (this.appliedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                      ? ''
                      : ' (s)')
                  "
                  :rules="[inputBinSizeRefFeedback]"
                  @change="
                    changeLastUpdated('Bin size', idWidgetHistogramViewRef)
                  "
                ></v-text-field>
                <v-text-field
                  dense
                  class="px-2"
                  v-model="
                    editedDashboardConfiguration.histogramNumberOfBinsRef
                  "
                  label="Number of bins in x axis"
                  :rules="[inputBinNumberRefFeedback]"
                  @change="
                    changeLastUpdated(
                      'Number of bins',
                      idWidgetHistogramViewRef
                    )
                  "
                ></v-text-field>
                <v-card-actions class="">
                  <v-btn
                    @click="updateHistogram(idWidgetHistogramViewRef)"
                    id="updateHistogramBtn"
                    color="primary"
                    :disabled="validHistogramRefInputs === false"
                    >Update histogram</v-btn
                  >
                </v-card-actions>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHistogramView === true"
        >
          <v-card outlined>
            <v-card-title>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptionsOther.concat(allMeshesIndex)"
                v-model="statisticsHistogramMeshIndexOther"
                label="Displayed mesh"
                v-if="availableMeshOptionsOther.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item, otherResultsLog.results)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item, otherResultsLog.results)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHistogramViewOther"
              :ref="idWidgetHistogramViewOther"
            ></div>
            <v-card-actions>
              <v-row
                no-gutters
                class="px-6"
                v-if="displayHistogramOptions === true && dataReady === true"
              >
                <v-text-field
                  dense
                  class="px-2"
                  v-model="editedDashboardConfiguration.histogramBinSizeOther"
                  :label="
                    'Size of bins in x axis' +
                    (this.appliedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                      ? ''
                      : ' (s)')
                  "
                  :rules="[inputBinSizeOtherFeedback]"
                  @change="
                    changeLastUpdated('Bin size', idWidgetHistogramViewOther)
                  "
                ></v-text-field>
                <v-text-field
                  dense
                  class="px-2"
                  v-model="
                    editedDashboardConfiguration.histogramNumberOfBinsOther
                  "
                  label="Number of bins in x axis"
                  :rules="[inputBinNumberOtherFeedback]"
                  @change="
                    changeLastUpdated(
                      'Number of bins',
                      idWidgetHistogramViewOther
                    )
                  "
                ></v-text-field>
                <v-card-actions class="">
                  <v-btn
                    @click="updateHistogram(idWidgetHistogramViewOther)"
                    id="updateHistogramBtn"
                    color="primary"
                    :disabled="validHistogramOtherInputs === false"
                    >Update histogram</v-btn
                  >
                </v-card-actions>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="
            appliedDashboardConfiguration.displayGlobalHistogramView === true
          "
        >
          <v-card outlined>
            <div
              :key="updateIndex"
              :id="idWidgetGlobalHistogramViewRef"
              :ref="idWidgetGlobalHistogramViewRef"
            ></div>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="
            appliedDashboardConfiguration.displayGlobalHistogramView === true
          "
        >
          <v-card outlined>
            <div
              :key="updateIndex"
              :id="idWidgetGlobalHistogramViewOther"
              :ref="idWidgetGlobalHistogramViewOther"
            ></div>
          </v-card>
        </v-col>
      </v-row>

      <v-dialog v-model="settingsDialog" width="800">
        <v-card v-if="this.settingsDialog === true">
          <!-- very important to disable if not displayed : the slider component would update zbound otherwize -->
          <v-card-title class="pb-0">
            <v-col>
              <v-row align="center">
                <icon scale="1.5" class="blue--text lighten-4" name="cogs" />
                <v-toolbar-title class="grey--text title"
                  >Display settings</v-toolbar-title
                >
              </v-row>
            </v-col>
          </v-card-title>
          <v-card-text>
            <exo-card-form label="Data filter"
              ><v-row no-gutters>
                <v-checkbox
                  v-model="editedDashboardConfiguration.enableDataFilter"
                  :label="
                    'Data filter [' +
                    minZInConfiguration +
                    ',' +
                    maxZInConfiguration +
                    '] ' +
                    this.unitTypes[this.editedDashboardConfiguration.currentMap]
                  "
                  class="mt-5 mb-0 pt-0 pb-0"
                >
                </v-checkbox>
                <v-col cols="4">
                  <v-btn
                    class="mx-5 my-3 mb-0 pt-0 pb-0 mx-6"
                    outlined
                    small
                    color="primary"
                    :disabled="!editedDashboardConfiguration.enableDataFilter"
                    @click="
                      setFilterMinValue(
                        Math.floor(
                          Math.min(
                            ...buildZDataUnfilteredNotNull(
                              currentResultsLog.results,
                              editedDashboardConfiguration.currentMap
                            )
                          )
                        )
                      )
                    "
                  >
                    Current mission min value
                  </v-btn>
                  <v-btn
                    class="mx-5 my-3 mb-0 pt-0 pb-0 mx-6"
                    outlined
                    small
                    color="primary"
                    :disabled="!editedDashboardConfiguration.enableDataFilter"
                    @click="
                      setFilterMinValue(
                        Math.floor(
                          Math.min(
                            ...buildZDataUnfilteredNotNull(
                              otherResultsLog.results,
                              editedDashboardConfiguration.currentMap
                            )
                          )
                        )
                      )
                    "
                  >
                    Other mission min value
                  </v-btn>
                </v-col>
                <v-col cols="4">
                  <v-btn
                    class="mx-5 my-3 mb-0 pt-0 pb-0 mx-6"
                    outlined
                    small
                    color="primary"
                    :disabled="!editedDashboardConfiguration.enableDataFilter"
                    @click="
                      setFilterMaxValue(
                        Math.ceil(
                          Math.max(
                            ...buildZDataUnfilteredNotNull(
                              currentResultsLog.results,
                              editedDashboardConfiguration.currentMap
                            )
                          )
                        )
                      )
                    "
                  >
                    Current mission max value
                  </v-btn>
                  <v-btn
                    class="mx-5 my-3 mb-0 pt-0 pb-0 mx-6"
                    outlined
                    small
                    color="primary"
                    :disabled="!editedDashboardConfiguration.enableDataFilter"
                    @click="
                      setFilterMaxValue(
                        Math.ceil(
                          Math.max(
                            ...buildZDataUnfilteredNotNull(
                              otherResultsLog.results,
                              editedDashboardConfiguration.currentMap
                            )
                          )
                        )
                      )
                    "
                  >
                    Other mission max value
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-range-slider
                  class="m-0 p-0 mt-4"
                  v-model="editedDashboardConfiguration.zBounds"
                  :disabled="!editedDashboardConfiguration.enableDataFilter"
                  :min="minZInConfiguration"
                  :max="maxZInConfiguration"
                  :step="stepData"
                  color="rgb(51, 204, 255)"
                  thumb-label="always"
                >
                  <template v-slot:thumb-label="item"
                    ><span style="color: black">{{
                      item.value
                    }}</span></template
                  >
                </v-range-slider>
              </v-row>
            </exo-card-form>
            <exo-card-form label="Maps">
              <v-row no-gutters>
                <v-checkbox
                  v-model="editedDashboardConfiguration.display3DView"
                  class="my-0 py-0 mr-4"
                  label="3D view"
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayMapView"
                  class="my-0 py-0 mr-4"
                  label="Mesh map"
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayHeatmapView"
                  class="my-0 py-0 mr-4"
                  label="Heatmap"
                ></v-checkbox>
              </v-row>
              <v-row no-gutters>
                <v-select
                  :items="colorsMaps"
                  v-model="editedDashboardConfiguration.currentColorMap"
                  label="Map colorscale"
                ></v-select>
              </v-row>
            </exo-card-form>
            <exo-card-form
              label="3D view settings"
              v-if="editedDashboardConfiguration.display3DView === true"
            >
              <v-row no-gutters>
                <v-checkbox
                  v-model="editedDashboardConfiguration.fullWidth3DView"
                  class="my-0 py-0 mr-4"
                  label="Full width"
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.interpolate"
                  class="my-0 py-0 mr-4"
                  label="Interpolate data"
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayGroundTrack"
                  class="my-0 py-0 mr-4"
                  label="Display ground track"
                ></v-checkbox>
              </v-row>
              <v-row
                no-gutters
                class="mt-0 pt-0"
                v-if="editedDashboardConfiguration.displayGroundTrack"
              >
                <v-select
                  class="mt-0 pt-0"
                  :items="availableOrbitsNumber"
                  v-model="editedDashboardConfiguration.numberOfOrbits"
                  label="Number of orbits to display"
                ></v-select>
              </v-row>
            </exo-card-form>
            <exo-card-form label="Statistics">
              <template v-slot:titleTooltip>
                <exo-help-dialog title="Histograms" :margin="'m-0'">
                  <tooltipHistograms />
                </exo-help-dialog>
              </template>
              <v-row no-gutters>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayGlobalHistogramView
                  "
                  class="my-0 py-0 mr-4"
                  label="Global Histogram"
                  :disabled="
                    editedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                  "
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayHistogramView"
                  class="my-0 py-0 mr-4"
                  label="Statistics Histogram"
                ></v-checkbox>
              </v-row>
            </exo-card-form>

            <exo-card-form
              label="Histogram settings"
              v-if="
                editedDashboardConfiguration.displayLocalHistogramView ===
                  true ||
                editedDashboardConfiguration.displayGlobalHistogramView ===
                  true ||
                editedDashboardConfiguration.displayHistogramView === true
              "
            >
              <template v-slot:titleTooltip>
                <exo-help-dialog title="Histograms settings" :margin="'m-0'">
                  <tooltipHistogramsSettings />
                </exo-help-dialog>
              </template>
              <v-row no-gutters>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayOccurenceHistogram
                  "
                  class="my-0 py-0 mr-4"
                  label="Display ocurrence histogram"
                ></v-checkbox>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayCumulativeHistogram
                  "
                  class="my-0 py-0 mr-4"
                  label="Display cumulative histogram"
                ></v-checkbox>
              </v-row>
              <exo-form-alert
                type="warning"
                v-if="this.isAnyHistogramSelected() === false"
              >
                Warning: no histogram was selected.
              </exo-form-alert>
            </exo-card-form>
          </v-card-text>
          <v-divider class="m-0"></v-divider>
          <v-card-actions class="mx-4 my-3">
            <v-btn @click="updateMap()" id="updateMapBtn" color="primary"
              >Update map</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import constellationUtils from '../../mixins/constellationUtils.js'
import Plotly from 'plotly.js-dist'
import * as d3 from 'd3'
import tooltipRevisitGroundSegments from '../../components/Tooltips/ConstellationModule/tooltipRevisitGroundSegments'
import tooltipCollectTime from '../../components/Tooltips/ConstellationModule/tooltipCollectTime'
import tooltipHistograms from '../../components/Tooltips/ConstellationModule/tooltipHistograms'
import tooltipHistogramsSettings from '../../components/Tooltips/ConstellationModule/tooltipHistogramsSettings'
import DashboardViewer from './DashboardViewer.vue'

Vue.use(dashboardComponents)

export default {
  name: 'ConstellationComparison',
  mixins: [errorMixin],

  components: {
    tooltipRevisitGroundSegments,
    tooltipCollectTime,
    tooltipHistograms,
    tooltipHistogramsSettings,
    'cesium-viewer': DashboardViewer
  },

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.reloadLayout()
  },

  beforeDestroy() {
    // When switching page, purge plotly context to avoid memory leaks and errors when resizing page
    // No catch if the plot didn't rendered
    this.currentDataRef = null
    this.currentDataOther = null
    this.emptyResult = false
    this.cleanViews()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
  },

  watch: {
    updateIndex(val) {
      if (this.dataLoaded) {
        Vue.nextTick(() => {
          this.prepareAndDisplayData()
        })
      }
    },
    minZInConfiguration() {
      Vue.set(this.editedDashboardConfiguration, 'zBounds', [
        this.minZInConfiguration,
        this.maxZInConfiguration
      ])
      // not refreshing correctly this.editedDashboardConfiguration.zBounds[0] = this.minZInConfiguration
    },
    maxZInConfiguration() {
      Vue.set(this.editedDashboardConfiguration, 'zBounds', [
        this.minZInConfiguration,
        this.maxZInConfiguration
      ])
      // not refreshing correctly this.editedDashboardConfiguration.zBounds[1] = this.maxZInConfiguration
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog',
      missions: 'missions'
    }),

    getPlotBackgroundColor() {
      return this.$vuetify.theme.dark ? '#1e1e1e' : 'white'
    },
    getPlotTextColor() {
      return this.$vuetify.theme.dark ? 'white' : '#1e1e1e'
    },

    missionsToCompare() {
      return this.missions
        ?.filter(
          (mission) =>
            mission.typeName === 'ConstellationPerformanceAnalysisRequest' &&
            mission.id !== this.currentMission?.id
        )
        .map((mission) => ({ text: mission.name, value: mission }))
    },

    displayComputationWidget() {
      return dashboardTools.displayComputationWidget(this)
    },
    displayResult() {
      return dashboardTools.displayResult(this)
    },
    displayError() {
      return (
        this.displayErrorCurrent ||
        this.displayErrorOther ||
        this.displayErrorConsistency
      )
    },
    displayErrorCurrent() {
      return (
        this.currentResultsLog &&
        this.currentResultsLog.errors &&
        this.currentResultsLog.errors.length > 0
      )
    },
    displayErrorOther() {
      return (
        this.otherResultsLog &&
        this.otherResultsLog.errors &&
        this.otherResultsLog.errors.length > 0
      )
    },
    displayErrorConsistency() {
      return this.errorConsistencyMessage != null
    },
    displayWarning() {
      return (
        this.emptyResult === true ||
        this.displaySpatialAverageWarning === true ||
        this.warningOtherMission === true
      )
    },
    displaySpatialAverageWarning() {
      if (this.dataLoaded) {
        let isConstantStep = false
        for (const mesh of this.currentResultsLog.results.earthMeshes)
          isConstantStep = isConstantStep || this.isConstantLatitudeStep(mesh)
        for (const mesh of this.otherResultsLog.results.earthMeshes)
          isConstantStep = isConstantStep || this.isConstantLatitudeStep(mesh)
        const isSpatialAverage =
          this.appliedDashboardConfiguration.displayHistogramView ||
          this.appliedDashboardConfiguration.displayGlobalHistogramView

        return isConstantStep === true && isSpatialAverage === true
      } else {
        return false
      }
    },
    errorConsistencyMessage() {
      return null
    },
    dataLoaded() {
      if (this.resultLoaded) {
        if (
          this.currentResultsLog.errors.length === 0 &&
          this.otherResultsLog?.errors?.length === 0
        ) {
          return true // no error
        }
      }
      return false
    },
    dataReady() {
      return this.dataLoaded && this.currentDataRef != null && !this.emptyResult
    },

    loading() {
      return this.currentDataRef == null && this.dataLoaded
    },

    resultLoaded() {
      return (
        this.currentMission?.typeName ===
          'ConstellationPerformanceAnalysisRequest' &&
        this.currentResultsLog?.results != null &&
        this.currentResultsLog.errors != null
      )
    },

    mapCategories() {
      // intersection of the 2 results
      if (
        !this.currentResultsLog ||
        !this.currentResultsLog.results ||
        !this.otherResultsLog ||
        !this.otherResultsLog.results
      ) {
        return []
      }

      let sortedCategories = JSON.parse(
        JSON.stringify(this.currentResultsLog.results.mapCategories)
      )
      sortedCategories = sortedCategories.filter((category) =>
        this.otherResultsLog.results.mapCategories.includes(category)
      )
      sortedCategories.sort()
      return sortedCategories
    },

    availableMapTypes() {
      return this.listAvailableMapTypes(
        this.editedDashboardConfiguration.currentCategory
      )
    },

    commonPercentiles() {
      let intersection =
        this.currentResultsLog.results.percentilesValues.filter((value) =>
          this.otherResultsLog.results.percentilesValues.includes(value)
        )
      return intersection
    },

    availableMeshOptionsCurrent() {
      const nMeshes = this.currentResultsLog?.results?.earthMeshes?.length
      return [...Array(nMeshes).keys()]
    },

    availableMeshOptionsOther() {
      const nMeshes = this.otherResultsLog?.results?.earthMeshes?.length
      return [...Array(nMeshes).keys()]
    },

    minZInConfiguration() {
      if (
        this.currentResultsLog == null ||
        !this.currentResultsLog?.results == null
      ) {
        return this.defaultDashboardConfiguration.zBounds[0]
      }

      let zMinRef = Math.floor(
        Math.min(
          ...this.buildZDataUnfilteredNotNull(
            this.currentResultsLog.results,
            this.editedDashboardConfiguration.currentMap
          )
        )
      )

      if (
        this.otherResultsLog == null ||
        this.otherResultsLog?.results == null
      ) {
        return zMinRef
      }

      let zMinOther = Math.floor(
        Math.min(
          ...this.buildZDataUnfilteredNotNull(
            this.otherResultsLog.results,
            this.editedDashboardConfiguration.currentMap
          )
        )
      )
      let val

      if (zMinRef <= zMinOther) {
        val = zMinRef
      } else {
        val = zMinOther
      }

      if (val.toString().includes('Infinity')) {
        val = this.defaultDashboardConfiguration.zBounds[0]
      }
      return val
    },

    maxZInConfiguration() {
      if (
        this.currentResultsLog == null ||
        !this.currentResultsLog?.results == null
      ) {
        return this.defaultDashboardConfiguration.zBounds[1]
      }
      let zMaxRef = Math.ceil(
        Math.max(
          ...this.buildZDataUnfilteredNotNull(
            this.currentResultsLog.results,
            this.editedDashboardConfiguration.currentMap
          )
        )
      )

      if (
        this.otherResultsLog == null ||
        this.otherResultsLog?.results == null
      ) {
        return zMaxRef
      }

      let zMaxOther = Math.ceil(
        Math.max(
          ...this.buildZDataUnfilteredNotNull(
            this.otherResultsLog.results,
            this.editedDashboardConfiguration.currentMap
          )
        )
      )
      let val

      if (zMaxRef >= zMaxOther) {
        val = zMaxRef
      } else {
        val = zMaxOther
      }

      if (val.toString().includes('Infinity')) {
        val = this.defaultDashboardConfiguration.zBounds[1]
      }
      return val
    },

    stepData() {
      return Math.max(
        1,
        Math.ceil((this.maxZInConfiguration - this.minZInConfiguration) / 100)
      )
    },

    groundStationNames() {
      if (this.currentMission?.groundStations != null) {
        return this.currentMission.groundStations.map(
          (groundStation) => groundStation.name
        )
      }
      return []
    },

    startOfSimulationDate() {
      if (this.currentMission?.simulationDate) {
        return new Date(this.currentMission.simulationDate)
      } else {
        return new Date()
      }
    },

    endOfSimulationDate() {
      if (this.currentMission?.simulationDuration) {
        return this.computeDateFromDuration(
          this.currentMission.simulationDuration
        )
      } else {
        return new Date()
      }
    },

    groundStationTimelineAvailable() {
      // TODO enable timelines per result (ref/other)
      return this.currentResultsLog?.results?.stationVisibilityTimeline != null
    },

    inputBinSizeRefFeedback() {
      let feedback = true
      let binSize = Number(
        this.editedDashboardConfiguration.histogramBinSizeRef
      )

      if (binSize !== null) {
        if (isNaN(binSize) || binSize <= 0) {
          feedback = 'Please input a valid positive number.'
        }
      }
      return feedback
    },

    inputBinSizeOtherFeedback() {
      let feedback = true
      let binSize = Number(
        this.editedDashboardConfiguration.histogramBinSizeOther
      )

      if (binSize !== null) {
        if (isNaN(binSize) || binSize <= 0) {
          feedback = 'Please input a valid positive number.'
        }
      }
      return feedback
    },

    inputBinNumberRefFeedback() {
      let feedback = true
      let binNumber = Number(
        this.editedDashboardConfiguration.histogramNumberOfBinsRef
      )

      if (binNumber !== null) {
        if (isNaN(binNumber) || binNumber <= 1) {
          feedback = 'Please input a valid number >1.'
        }
      }
      return feedback
    },

    inputBinNumberOtherFeedback() {
      let feedback = true
      let binNumber = Number(
        this.editedDashboardConfiguration.histogramNumberOfBinsOther
      )

      if (binNumber !== null) {
        if (isNaN(binNumber) || binNumber <= 1) {
          feedback = 'Please input a valid number >1.'
        }
      }
      return feedback
    },

    validHistogramRefInputs() {
      if (this.lastHistogramOptionUpdatedRef == 'Bin size') {
        return this.inputBinSizeRefFeedback === true
      } else {
        return this.inputBinNumberRefFeedback === true
      }
    },

    validHistogramOtherInputs() {
      if (this.lastHistogramOptionUpdatedOther == 'Bin size') {
        return this.inputBinSizeOtherFeedback === true
      } else {
        return this.inputBinNumberOtherFeedback === true
      }
    },

    warningOtherMission() {
      if (this.otherResultsLog != null) {
        if (this.otherResultsLog.results == null) {
          return 'The mission does not have any results, please compute it first.'
        }
        let warnings = ''
        if (
          this.otherResultsLog?.results?.simulationDate !==
          this.currentResultsLog?.results?.simulationDate
        ) {
          warnings +=
            'The 2 constellations are analyzed at a different date, this might impact the results : ' +
            this.currentResultsLog?.results?.simulationDate +
            ' vs ' +
            this.otherResultsLog?.results?.simulationDate +
            '. '
        }
        if (
          this.otherResultsLog?.results?.simulationDuration !==
          this.currentResultsLog?.results?.simulationDuration
        ) {
          warnings +=
            'The 2 constellations are analyzed during a different duration, this might impact the results : ' +
            this.currentResultsLog?.results?.simulationDuration +
            's vs ' +
            this.otherResultsLog?.results?.simulationDuration +
            's. '
        }

        if (this.otherMission?.dirty === true) {
          warnings +=
            'You have changed some components of the other constellation. This results does not match the current inputs. Please recompute to update.'
        }
        if (warnings === '') {
          return null
        }
        return warnings
      } else {
        return null
      }
    },

    appliedMetricName() {
      if (
        this.appliedDashboardConfiguration.currentCategory ==
        'pointNumberOfAccesses'
      ) {
        return this.getResultsFieldLabel(
          this.appliedDashboardConfiguration.currentCategory
        )
      } else {
        return (
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentMap
          ) +
          ' ' +
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentCategory
          ).toLowerCase()
        )
      }
    }
  },

  data() {
    return {
      otherMission: null,
      otherResultsLog: null,

      editedDashboardConfiguration: {
        zBounds: [-1, 1],
        display3DView: true,
        displayMapView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSizeRef: '',
        histogramBinSizeOther: '',
        histogramNumberOfBinsRef: '',
        histogramNumberOfBinsOther: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        displayGroundTrack: false,
        enableDataFilter: false,
        numberOfOrbits: 0,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },
      appliedDashboardConfiguration: {
        zBounds: [-1, 1],
        display3DView: true,
        displayMapView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSizeRef: '',
        histogramBinSizeOther: '',
        histogramNumberOfBinsRef: '',
        histogramNumberOfBinsOther: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        displayGroundTrack: false,
        enableDataFilter: false,
        numberOfOrbits: 0,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },
      defaultDashboardConfiguration: {
        zBounds: [-1, 1],
        display3DView: true,
        displayMapView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSizeRef: '',
        histogramBinSizeOther: '',
        histogramNumberOfBinsRef: '',
        histogramNumberOfBinsOther: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        displayGroundTrack: false,
        enableDataFilter: false,
        numberOfOrbits: 0,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },

      ID_SEPARATOR: '#/#',
      currentTimelineLabel: null,
      timelineCategory: '',
      availableTimelines: [],
      timelineIntervals: {},
      idWidgetMapViewRef: 'plotingAreaGeoRef',
      idWidgetMapViewOther: 'plotingAreaGeoOther',
      idWidgetHeatmapViewRef: 'plotingAreaHeatRef',
      idWidgetHeatmapViewOther: 'plotingAreaHeatOther',
      idWidgetHistogramViewRef: 'plotingMetricHistogramRef',
      idWidgetHistogramViewOther: 'plotingMetricHistogramOther',
      idWidgetGlobalHistogramViewRef: 'plotingGlobalHistogramRef',
      idWidgetGlobalHistogramViewOther: 'plotingGlobalHistogramOther',
      buildingMap: false,
      constellationsNameByIndexRef: {},
      constellationsNameByIndexOther: {},
      dialog: false,
      settingsDialog: false,
      terrainProvider: null,
      viewerRef: null,
      viewerOther: null,
      rectangles: [],
      factorX: 16,
      factorY: 16,
      mapboxToken:
        'pk.eyJ1IjoiZXhvdHJhaWwiLCJhIjoiY2szcTNnZmFkMDkzbzNtcDhzMm0ydmJ5YyJ9.5so1MY_ou6yoPmLAzLchUg',
      label: null,
      mapTypes: [
        'pointSimultaneousSpacecraftsAverage',
        'pointSimultaneousSpacecraftsMedian',
        'pointSimultaneousSpacecraftsMax',
        'pointSimultaneousSpacecraftsMin',
        'pointSimultaneousSpacecraftsPercentile',
        'pointCollectTimeAverage',
        'pointCollectTimeMedian',
        'pointCollectTimeMax',
        'pointCollectTimeMin',
        'pointCollectTimePercentile',
        'pointAccessDurationAverage',
        'pointAccessDurationMedian',
        'pointAccessDurationMax',
        'pointAccessDurationMin',
        'pointAccessDurationPercentile',
        'pointAccessDurationCumulative',
        'pointRevisitTimeAverage',
        'pointRevisitTimeMedian',
        'pointRevisitTimeMax',
        'pointRevisitTimeMin',
        'pointRevisitTimePercentile',
        'forwardToReverseLinkTimeAverage',
        'forwardToReverseLinkTimeMedian',
        'forwardToReverseLinkTimeMax',
        'forwardToReverseLinkTimeMin',
        'forwardToReverseLinkTimePercentile',
        'reverseLinkTransferTimeAverage',
        'reverseLinkTransferTimeMedian',
        'reverseLinkTransferTimeMax',
        'reverseLinkTransferTimeMin',
        'reverseLinkTransferTimePercentile',
        'forwardLinkTransferTimeAverage',
        'forwardLinkTransferTimeMedian',
        'forwardLinkTransferTimeMax',
        'forwardLinkTransferTimeMin',
        'forwardLinkTransferTimePercentile',
        'timeBetweenReverseLinkArrivalsAverage',
        'timeBetweenReverseLinkArrivalsMedian',
        'timeBetweenReverseLinkArrivalsMax',
        'timeBetweenReverseLinkArrivalsMin',
        'timeBetweenReverseLinkArrivalsPercentile',
        'pointNumberOfAccesses'
      ],
      unitTypes: {
        pointAccessDurationAverage: 's',
        pointAccessDurationMedian: 's',
        pointAccessDurationMax: 's',
        pointAccessDurationMin: 's',
        pointAccessDurationPercentile: 's',
        pointAccessDurationCumulative: 's',
        pointRevisitTimeAverage: 's',
        pointRevisitTimeMedian: 's',
        pointRevisitTimeMax: 's',
        pointRevisitTimeMin: 's',
        pointRevisitTimePercentile: 's',
        pointSimultaneousSpacecraftsAverage: '',
        pointSimultaneousSpacecraftsMedian: '',
        pointSimultaneousSpacecraftsMax: '',
        pointSimultaneousSpacecraftsMin: '',
        pointSimultaneousSpacecraftsPercentile: '',
        pointCollectTimeAverage: 's',
        pointCollectTimeMedian: 's',
        pointCollectTimeMax: 's',
        pointCollectTimeMin: 's',
        pointCollectTimePercentile: 's',
        forwardToReverseLinkTimeAverage: 's',
        forwardToReverseLinkTimeMedian: 's',
        forwardToReverseLinkTimeMax: 's',
        forwardToReverseLinkTimeMin: 's',
        forwardToReverseLinkTimePercentile: 's',
        reverseLinkTransferTimeAverage: 's',
        reverseLinkTransferTimeMedian: 's',
        reverseLinkTransferTimeMax: 's',
        reverseLinkTransferTimeMin: 's',
        reverseLinkTransferTimePercentile: 's',
        forwardLinkTransferTimeAverage: 's',
        forwardLinkTransferTimeMedian: 's',
        forwardLinkTransferTimeMax: 's',
        forwardLinkTransferTimeMin: 's',
        forwardLinkTransferTimePercentile: 's',
        timeBetweenReverseLinkArrivalsAverage: 's',
        timeBetweenReverseLinkArrivalsMedian: 's',
        timeBetweenReverseLinkArrivalsMax: 's',
        timeBetweenReverseLinkArrivalsMin: 's',
        timeBetweenReverseLinkArrivalsPercentile: 's',
        pointNumberOfAccesses: ''
      },
      availableOrbitsNumber: [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15
      ],
      updateIndex: 0,
      colorsMaps: ['Viridis', 'Magma', 'YlGnBu', 'Jet'],
      heatMapCommons: {
        colorScale: [],
        currentGradient: [],
        gradientStep: 0,
        tickText: [],
        tickValues: [],
        zMax: 10e3,
        zMin: 0
      },
      displayHistogramOptions: true,
      lastHistogramOptionUpdatedRef: '',
      lastHistogramOptionUpdatedOther: '',
      lastHistogramIdUpdated: '',
      currentDataRef: null,
      currentDataOther: null,
      emptyResult: false,
      ellipsoid: null,
      heatmapMeshIndexCurrent: 0,
      heatmapMeshIndexOther: 0,
      statisticsHistogramMeshIndexCurrent: 0,
      statisticsHistogramMeshIndexOther: 0,
      allMeshesIndex: 'All meshes'
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      this.reloadLayout()
    },

    reloadLayout() {
      setTimeout(() => {
        this.loadLayout()
      }, 1)
    },

    async changeOtherMission() {
      // load other computation log
      if (this.otherMission?.computationLogId != null) {
        try {
          let response = await this.$http.get(
            `users/${this.currentUser.id}/computationlogs/${this.otherMission.computationLogId}`
          )
          this.otherResultsLog = await response.data
        } catch (e) {
          this.displayMessage(
            'Error getting results',
            e,
            'notification',
            'error'
          )
          this.otherResultsLog = null
        }
      } else {
        this.otherResultsLog = null
      }
      this.reloadLayout()
    },

    meshIndexUpdated() {
      this.updateMap()
    },

    listAvailableMapTypes(currentCategory) {
      return this.mapTypes.filter((el) => el.startsWith(currentCategory))
    },

    openDashboardSettings() {
      this.editedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.appliedDashboardConfiguration)
      )
      if (
        this.editedDashboardConfiguration.zBounds[0] ===
          this.defaultDashboardConfiguration.zBounds[0] &&
        this.editedDashboardConfiguration.zBounds[1] ===
          this.defaultDashboardConfiguration.zBounds[1]
      ) {
        Vue.set(this.editedDashboardConfiguration, 'zBounds', [
          this.minZInConfiguration,
          this.maxZInConfiguration
        ])
      }

      this.settingsDialog = true
    },

    setFilterMinValue(value) {
      if (!isNaN(Number(value))) {
        Vue.set(this.editedDashboardConfiguration, 'zBounds', [
          value - this.stepData,
          this.editedDashboardConfiguration.zBounds[1]
        ])
      }
    },

    setFilterMaxValue(value) {
      if (!isNaN(Number(value))) {
        Vue.set(this.editedDashboardConfiguration, 'zBounds', [
          this.editedDashboardConfiguration.zBounds[0],
          value + this.stepData
        ])
      }
    },

    buildZDataUnfiltered(results, currentMap) {
      if (!results || !results.meshPerformances) {
        return []
      }
      const data = []

      let iLon = 0
      let row = []
      const self = this
      results.meshPerformances.forEach(function (mesh, index) {
        const meshData = []
        const nLongitudes = results.earthMeshes[index].longitudes.length
        for (const point of mesh.points) {
          const value = self.getAppliedMetricValue(point, currentMap)
          row.push(value)
          iLon += 1
          if (iLon >= nLongitudes) {
            meshData.push(row)
            row = []
            iLon = 0
          }
        }

        data.push(meshData)
      })

      return data
    },

    getAppliedMetricValue(point, currentMap) {
      if (currentMap == 'pointNumberOfAccesses') {
        return point.metrics.numberOfAccesses
      }

      const metricName = this.getStatisticalMetricName(currentMap)
      const metricStatistic = this.getStatisticalMetricStatistic(currentMap)
      if (
        !(metricName in point.metrics.statisticalMetrics) ||
        !(metricStatistic in point.metrics.statisticalMetrics[metricName])
      ) {
        return null
      }

      let value = point.metrics.statisticalMetrics[metricName][metricStatistic]

      if (value != null && metricStatistic === 'percentiles') {
        value = value[this.appliedDashboardConfiguration.currentPercentileIndex]
      }

      value = value === 'NaN' || value === 'Infinity' ? null : value
      return value
    },

    buildZDataUnfilteredNotNull(results, currentMap) {
      const data = []
      let zData = this.buildZDataUnfiltered(results, currentMap)
      results.earthMeshes.forEach(function (mesh, index) {
        data.push({
          x: mesh.longitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          y: mesh.latitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          z: zData[index]
        })
      })

      return this.zNotNull(data)
    },

    computeDateFromDuration(secondsSinceStart) {
      if (this.currentMission?.simulationDate) {
        let newDate = new Date(this.currentMission.simulationDate)
        newDate.setSeconds(newDate.getSeconds() + secondsSinceStart)
        return newDate
      } else {
        return new Date()
      }
    },

    isAnyHistogramSelected() {
      return (
        this.editedDashboardConfiguration.displayOccurenceHistogram === true ||
        this.editedDashboardConfiguration.displayCumulativeHistogram === true
      )
    },

    resetLayout() {
      this.saveLayout(this.defaultDashboardConfiguration)
    },
    saveCurrentLayout() {
      this.saveLayout(this.appliedDashboardConfiguration)
    },

    saveLayout(layout) {
      this.currentMission.layout = JSON.stringify(layout)
      this.$http
        .put(
          dashboardTools.getCurrentAPI(this) +
            '/' +
            this.currentMission.id +
            '/minorupdate',
          this.currentMission
        )
        .then((response) => {
          this.$root.$emit('set-current-mission', response.data)
        })
    },

    cleanPlotlyComponent(component) {
      try {
        Plotly.purge(this.$refs[component])
      } catch (e) {
        // might not exists0
      }
    },

    cleanMapView() {
      this.cleanPlotlyComponent(this.idWidgetMapViewRef)
      this.cleanPlotlyComponent(this.idWidgetMapViewOther)
    },

    cleanHeatmapView() {
      this.cleanPlotlyComponent(this.idWidgetHeatmapViewRef)
      this.cleanPlotlyComponent(this.idWidgetHeatmapViewOther)
    },

    cleanHistogramView() {
      this.cleanPlotlyComponent(this.idWidgetHistogramViewRef)
      this.cleanPlotlyComponent(this.idWidgetHistogramViewOther)
    },

    cleanGlobalHistogramView() {
      this.cleanPlotlyComponent(this.idWidgetGlobalHistogramViewRef)
      this.cleanPlotlyComponent(this.idWidgetGlobalHistogramViewOther)
    },

    clean3DView() {
      delete this.viewerRef
      delete this.viewerOther
    },

    loadLayout() {
      if (this.dataLoaded) {
        this.editedDashboardConfiguration = JSON.parse(
          JSON.stringify(this.defaultDashboardConfiguration)
        )

        if (this.currentMission.layout) {
          const savedConfiguration = JSON.parse(this.currentMission.layout)
          for (let field of Object.keys(this.editedDashboardConfiguration)) {
            if (savedConfiguration[field] != null) {
              this.editedDashboardConfiguration[field] =
                savedConfiguration[field]
            }
          }
        }
        Vue.nextTick(() => {
          this.updateMap()
        })
      }
    },

    async computeGraphData(iLat, iLon) {
      const singleSimulation = {
        simulation: this.currentMission, // earth radius
        iLat: iLat,
        iLon: iLon
      }
      let data = await this.$http
        .post('/physics/computeraan', singleSimulation)
        .then(function (response) {
          return response.data
        })
        .catch((e) => {})
      return data
    },

    valueToUnitText(value) {
      if (
        this.unitTypes[this.appliedDashboardConfiguration.currentMap] === 's'
      ) {
        return value != null && !isNaN(value)
          ? dataTools.sec2DDHHMMSS(value)
          : 'undefined'
      } else if (
        this.appliedDashboardConfiguration.currentCategory ===
        'pointSimultaneousSpacecrafts'
      ) {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed(1)
        } else {
          return 'undefined'
        }
      } else {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed()
        } else {
          return 'undefined'
        }
      }
    },

    requestComputation() {
      this.$router.push({ path: '/analyticalconstellationsimulation' })
      Vue.nextTick(() => {
        // use nextTick to be sure mission components are created and can handle events
        this.$root.$emit('request-computation-constellation-analysis')
      })
    },

    updateMap() {
      this.cleanViews()
      this.settingsDialog = false

      this.appliedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.editedDashboardConfiguration)
      )

      if (this.appliedDashboardConfiguration.interpolate) {
        this.factorX = 1
        this.factorY = 1
      } else {
        this.factorX = 16
        this.factorY = 16
      }
      this.buildingMap = true
      if (this.isAnyHistogramSelected() === false) {
        this.appliedDashboardConfiguration.displayHistogramView = false
        this.appliedDashboardConfiguration.displayGlobalHistogramView = false
      } else if (
        this.appliedDashboardConfiguration.currentCategory ==
        'pointNumberOfAccesses'
      ) {
        this.appliedDashboardConfiguration.displayGlobalHistogramView = false
      }

      this.updateIndex++
    },

    updateHistogram(histogramView) {
      this.appliedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.editedDashboardConfiguration)
      )

      // correct zbounds before upadteing the histogram
      if (
        this.appliedDashboardConfiguration.zBounds != null &&
        this.appliedDashboardConfiguration.zBounds[0] !== -1
      ) {
        if (
          this.appliedDashboardConfiguration.zBounds[0] <
            this.minZInConfiguration ||
          this.appliedDashboardConfiguration.zBounds[1] >
            this.maxZInConfiguration
        ) {
          this.appliedDashboardConfiguration.zBounds[0] =
            this.minZInConfiguration
          this.appliedDashboardConfiguration.zBounds[1] =
            this.maxZInConfiguration
        }
      }

      let xbinsSize
      let dataRange

      if (histogramView == this.idWidgetHistogramViewRef) {
        dataRange =
          Math.ceil(Math.max(...this.zNotNull(this.currentDataRef))) -
          Math.floor(Math.min(...this.zNotNull(this.currentDataRef)))
        if (this.lastHistogramOptionUpdatedRef == 'Bin size') {
          xbinsSize = this.editedDashboardConfiguration.histogramBinSizeRef
          this.editedDashboardConfiguration.histogramNumberOfBinsRef =
            Math.ceil(dataRange / xbinsSize)
          this.appliedDashboardConfiguration.histogramNumberOfBinsRef =
            Math.ceil(dataRange / xbinsSize)
        } else {
          xbinsSize =
            dataRange /
            Math.ceil(
              this.appliedDashboardConfiguration.histogramNumberOfBinsRef
            )
          if (xbinsSize < 1) {
            xbinsSize = 1
            this.editedDashboardConfiguration.histogramNumberOfBinsRef =
              Math.ceil(dataRange / xbinsSize)
            this.appliedDashboardConfiguration.histogramNumberOfBinsRef =
              Math.ceil(dataRange / xbinsSize)
          }
          this.editedDashboardConfiguration.histogramBinSizeRef =
            Math.round(xbinsSize)
          this.appliedDashboardConfiguration.histogramBinSizeRef =
            Math.round(xbinsSize)
        }
      } else {
        dataRange =
          Math.ceil(Math.max(...this.zNotNull(this.currentDataOther))) -
          Math.floor(Math.min(...this.zNotNull(this.currentDataOther)))
        if (this.lastHistogramOptionUpdatedOther == 'Bin size') {
          xbinsSize = this.editedDashboardConfiguration.histogramBinSizeOther
          this.editedDashboardConfiguration.histogramNumberOfBinsOther =
            Math.ceil(dataRange / xbinsSize)
          this.appliedDashboardConfiguration.histogramNumberOfBinsOther =
            Math.ceil(dataRange / xbinsSize)
        } else {
          xbinsSize =
            dataRange /
            Math.ceil(
              this.appliedDashboardConfiguration.histogramNumberOfBinsOther
            )
          if (xbinsSize < 1) {
            xbinsSize = 1
            this.editedDashboardConfiguration.histogramNumberOfBinsOther =
              Math.ceil(dataRange / xbinsSize)
            this.appliedDashboardConfiguration.histogramNumberOfBinsOther =
              Math.ceil(dataRange / xbinsSize)
          }
          this.editedDashboardConfiguration.histogramBinSizeOther =
            Math.round(xbinsSize)
          this.appliedDashboardConfiguration.histogramBinSizeOther =
            Math.round(xbinsSize)
        }
      }
      if (xbinsSize == Infinity) {
        xbinsSize = ''
      }
      Plotly.restyle(histogramView, { 'xbins.size': xbinsSize })
    },

    changeLastUpdated(lastHistogramOptionUpdated, lastHistogramIdUpdated) {
      this.lastHistogramIdUpdated = lastHistogramIdUpdated
      if (lastHistogramIdUpdated == this.idWidgetHistogramViewRef) {
        this.lastHistogramOptionUpdatedRef = lastHistogramOptionUpdated
      } else {
        this.lastHistogramOptionUpdatedOther = lastHistogramOptionUpdated
      }
    },

    generateData() {
      this.currentDataRef = this.generateDataFromResults(
        this.currentResultsLog.results
      )
      this.currentDataOther = this.generateDataFromResults(
        this.otherResultsLog.results
      )
    },

    generateDataFromResults(results) {
      const data = []
      if (!this.currentResultsLog.results.meshPerformances) {
        return data
      }
      let zData = this.buildZDataUnfiltered(
        results,
        this.appliedDashboardConfiguration.currentMap
      )
      if (
        this.appliedDashboardConfiguration?.zBounds != null &&
        this.appliedDashboardConfiguration.enableDataFilter
      ) {
        if (this.appliedDashboardConfiguration.zBounds[0] !== -1) {
          const min = this.appliedDashboardConfiguration.zBounds[0]
          const max = this.appliedDashboardConfiguration.zBounds[1]
          zData = zData.map((mesh) =>
            mesh.map((row) =>
              row.map((el) => {
                return el > max || el < min ? null : el
              })
            )
          )
        }
      }
      results.earthMeshes.forEach(function (mesh, index) {
        data.push({
          x: mesh.longitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          y: mesh.latitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          z: zData[index]
        })
      })

      return data
    },

    generateHistogramDataFromResults(results) {
      let histogramData = {}
      let iBin = 0
      let binStart = []
      let binEnd = []
      let binMiddle = []
      let binValue = []
      let binValueCumulative = []
      let applyFilter = false
      let isWithinFilter = true

      if (isNaN(results[0][0])) {
        return null
      }

      if (this.appliedDashboardConfiguration?.zBounds != null) {
        if (this.appliedDashboardConfiguration.zBounds[0] !== -1) {
          applyFilter = true
        }
      }

      applyFilter = false //TODO change a volonte
      for (iBin = 0; iBin < results.length; iBin++) {
        isWithinFilter =
          results[iBin][0] > this.appliedDashboardConfiguration.zBounds[0] &&
          results[iBin][1] < this.appliedDashboardConfiguration.zBounds[1]
        if (applyFilter === false || isWithinFilter === true) {
          binStart.push(results[iBin][0])
          binEnd.push(results[iBin][1])
          binMiddle.push(
            0.5 * (binStart[binStart.length - 1] + binEnd[binEnd.length - 1])
          )
          binValue.push(results[iBin][2])
          if (binValueCumulative.length > 0) {
            binValueCumulative.push(
              binValueCumulative[binValueCumulative.length - 1] +
                binValue[binValue.length - 1]
            )
          } else {
            binValueCumulative.push(binValue[binValue.length - 1])
          }
        }
      }

      histogramData['binStart'] = binStart
      histogramData['binEnd'] = binEnd
      histogramData['binMiddle'] = binMiddle
      histogramData['binValue'] = binValue
      histogramData['binValueCumulative'] = binValueCumulative

      return histogramData
    },

    zNotNull(dataset) {
      let zNotNull = []

      dataset.forEach((mesh) => {
        const zData = mesh.z
        zData.forEach((row) =>
          row.forEach((val) => {
            if (val != null) {
              zNotNull.push(val)
            }
          })
        )
      })
      return zNotNull
    },

    generateGradient() {
      this.heatMapCommons.currentGradient = constellationUtils.getGradient(
        this.appliedDashboardConfiguration.currentColorMap
      )
      this.heatMapCommons.colorscale = this.generatePlotlyColorScale()

      this.heatMapCommons.zMin = this.zDataFilteredAbsoluteMin()
      this.heatMapCommons.zMax = this.zDataFilteredAbsoluteMax()
      this.emptyResult = this.zNotNull(this.currentDataRef).length === 0
      this.generatePlotlyHeatmapTicks()
    },

    zDataFilteredAbsoluteMax() {
      let zMaxRef = Math.ceil(Math.max(...this.zNotNull(this.currentDataRef)))
      let zMaxOther = Math.ceil(
        Math.max(...this.zNotNull(this.currentDataOther))
      )

      if (zMaxRef >= zMaxOther) {
        return zMaxRef
      } else {
        return zMaxOther
      }
    },

    zDataFilteredAbsoluteMin() {
      let zMinRef = Math.floor(Math.min(...this.zNotNull(this.currentDataRef)))
      let zMinOther = Math.floor(
        Math.min(...this.zNotNull(this.currentDataOther))
      )

      if (zMinRef <= zMinOther) {
        return zMinRef
      } else {
        return zMinOther
      }
    },

    categoryUpdated() {
      this.updateDefaultMapFromCategory(this.editedDashboardConfiguration)
    },

    updateDefaultMapFromCategory(configuration) {
      if (this.currentResultsLog?.results?.mapCategories == null) {
        configuration.currentCategory = null
        configuration.currentMap = null
        return
      }
      if (
        configuration.currentCategory == null ||
        !this.currentResultsLog.results.mapCategories.includes(
          configuration.currentCategory
        )
      ) {
        configuration.currentCategory =
          this.currentResultsLog.results.mapCategories[0]
      }
      let currentMapTypes = this.listAvailableMapTypes(
        configuration.currentCategory
      )
      if (
        configuration.currentMap == null ||
        !currentMapTypes.includes(configuration.currentMap)
      ) {
        configuration.currentMap = currentMapTypes[0]
      }
    },

    buildConstellationsNameByIndex(suffix) {
      let index = 0
      const resultsLog = this.getResultsLogFromSuffix(suffix)
      resultsLog.results.homogeneousConstellationGeometries.forEach(
        (geometry) => {
          let planeIndex = 0
          while (planeIndex < geometry.numberOfPlanes) {
            this['constellationsNameByIndex' + suffix][index++] =
              geometry.name + ' - Plane ' + (planeIndex + 1)
            planeIndex++
          }
        }
      )
    },

    convertToHourFormat(seconds) {
      let hours = Math.floor(seconds / 3600)
      let minutes = Math.floor((seconds - hours * 3600) / 60)
      let second = Math.floor(seconds - hours * 3600 - minutes * 60)

      return String(hours + 'h ' + minutes + 'min ' + second) + 's '
    },

    displaySinglePointResults(lat, lon) {
      this.cleanTimelines()
      let lonStr = dataTools.formatAngle(dataTools.rad2deg(lon))
      let latStr = dataTools.formatAngle(dataTools.rad2deg(lat))
      this.timelineCategory =
        'Revisit on latitude ' + latStr + ' / longitude ' + lonStr
      let self = this
      self.loadingSinglePointResults = true
      this.$http
        .post(
          dashboardTools.getCurrentAPI(this) +
            '/single_point/' +
            this.currentMission.id,
          { lat: lat, lon: lon }
        )
        .then((response) => {
          for (let key of Object.keys(response.data.revisitTimeline)) {
            const isDefault = key.startsWith('All')
            this.addTimeline(key, response.data.revisitTimeline[key], isDefault)
          }

          // if (
          //   self.appliedDashboardConfiguration.currentCategory !=
          //     'pointNumberOfAccesses' &&
          //   self.isAnyHistogramSelected() === true
          // ) {
          //   self.drawLocalHistogram(response.data.globalHistograms)
          // }
          self.loadingSinglePointResults = false
        })
    },

    displayGroundStationTimeline(stationName) {
      // TODO enable timelines per result (ref/other)
      if (this.groundStationTimelineAvailable) {
        this.cleanTimelines()
        this.timelineCategory = 'Ground station visibility'
        const keys = Object.keys(
          this.currentResultsLog.results.stationVisibilityTimeline
        )
        for (let key of keys) {
          const keyElements = key.split(this.ID_SEPARATOR)
          const groundStationName = keyElements[0]
          const constellationName = keyElements[1]
          if (groundStationName === stationName) {
            const isDefault = constellationName.startsWith('All')
            this.addTimeline(
              key,
              this.currentResultsLog.results.stationVisibilityTimeline[key],
              isDefault
            )
          }
        }
      }
    },

    addTimeline(key, intervals, isDefault) {
      const regex = new RegExp(this.ID_SEPARATOR, 'g')
      const label = key.replace(regex, ' - ')
      this.availableTimelines.push(label)
      this.availableTimelines.sort()
      this.timelineIntervals[label] = intervals
      if (isDefault || this.currentTimelineLabel == null) {
        this.currentTimelineLabel = label
        this.updateTimeline()
      }
    },

    cleanTimelines() {
      this.currentTimelineLabel = null
      this.availableTimelines = []
      this.timelineIntervals = {}
      this.timelineCategory = ''
      this.cleanTimelineView()
    },

    cleanTimelineView() {
      d3.select('#timeline').selectAll('*').remove().selectAll('rect').remove()
    },

    updateTimeline() {
      // TODO check display is UTC
      // TODO detail date and durration when over
      const intervals = this.timelineIntervals[this.currentTimelineLabel]
      this.cleanTimelineView()

      const margin = 25
      const svgWidth = document.getElementById('timeline').offsetWidth
      const svgHeight = 60
      let chartWidth = svgWidth - 2 * margin
      let chartHeight = 40

      let zoom = d3
        .zoom()
        .scaleExtent([0.5, 20]) // This control how much you can unzoom (x0.5) and zoom (x20)
        .extent([
          [0, 0],
          [chartWidth, chartHeight]
        ])
        .on('zoom', updateChart)

      let svg = d3
        .select('#timeline')
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .call(zoom)

      let chart = svg.append('g').attr('transform', `translate(${margin}, 0)`)

      // zoom not used
      // this.brush = d3.brushX()
      //  .on('brush', this.setSelection)
      /*
      chart
        .append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .call(this.brush.move, [0, 100])
        */
      let scaleX = d3
        .scaleTime()
        .range([0, chartWidth])
        .domain([this.startOfSimulationDate, this.endOfSimulationDate])

      let xAxis = svg
        .append('g')

        .attr('transform', `translate(0, ${chartHeight})`)
        .call(d3.axisBottom(scaleX))

      // create a tooltip
      const div = d3
        .select('#timeline')
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('border-radius', '4px')
        .style('background-color', 'black')
        .style('font', '12px')
        .style('color', 'white')
        .style('padding', '5px')
        .style('position', 'absolute')

      const self = this

      chart
        .selectAll('rect')
        .data(intervals)
        .enter()
        .append('rect')
        .attr('x', (interval) => {
          return scaleX(this.computeDateFromDuration(interval.start)) - margin
        })
        .attr('y', 0)
        .attr('width', (interval) => {
          let width =
            scaleX(this.computeDateFromDuration(interval.end)) -
            scaleX(this.computeDateFromDuration(interval.start))
          width = width > 0 ? width : 1
          return width
        })
        .attr('height', chartHeight)
        .style('fill', this.$vuetify.theme.dark ? 'white' : 'black')
        .on('mouseover', function (interval) {
          div.transition().duration(200).style('opacity', 0.9)
          div
            .html(
              'Length : ' +
                self.convertToHourFormat(interval.end - interval.start) +
                '<br>' +
                'Start: ' +
                self.computeDateFromDuration(interval.start) +
                '<br>' +
                'End: ' +
                self.computeDateFromDuration(interval.end)
            )
            .style('left', d3.event.pageX - 40 + 'px')
            .style('top', d3.event.pageY - 80 + 'px')
        })
        .on('mouseout', function (d) {
          div.transition().duration(200).style('opacity', 0)
          div.html('')
        })

      function updateChart() {
        // recover the new scale
        let newX = d3.event.transform.rescaleX(scaleX)

        // update axes with these new boundaries
        xAxis.call(d3.axisBottom(newX))

        chart
          .selectAll('rect')
          .data(intervals)
          .attr('x', function (interval) {
            return newX(self.computeDateFromDuration(interval.start)) - margin
          })
          .attr('width', (interval) => {
            let width =
              newX(self.computeDateFromDuration(interval.end)) -
              newX(self.computeDateFromDuration(interval.start))
            width = width > 0 ? width : 1

            return width
          })
      }
    },

    getMissionFromSuffix(suffix) {
      return suffix === 'Other' ? this.otherMission : this.currentMission
    },

    getResultsLogFromSuffix(suffix) {
      return suffix === 'Other' ? this.otherResultsLog : this.currentResultsLog
    },

    // addTimelineBuilder(viewer) {
    //   const self = this
    //   const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas)
    //   const ellipsoid = this.ellipsoid

    //   // revisit
    //   handler.setInputAction(function (movement) {
    //     let mousePosition = new Cesium.Cartesian2(
    //       movement.position.x,
    //       movement.position.y
    //     )
    //     let cartesian = viewer.camera.pickEllipsoid(mousePosition, ellipsoid)
    //     if (cartesian) {
    //       let cartographic = ellipsoid.cartesianToCartographic(cartesian)
    //       self.displaySinglePointResults(
    //         cartographic.latitude,
    //         cartographic.longitude
    //       )
    //     } // else Globe was not picked
    //   }, Cesium.ScreenSpaceEventType.RIGHT_CLICK)

    //   // ground stations
    //   handler.setInputAction(function (movement) {
    //     let pickedObject = viewer.scene.pick(movement.position)
    //     if (Cesium.defined(pickedObject)) {
    //       const objectName = pickedObject.id._name
    //       for (let groundStationName of self.groundStationNames) {
    //         if (objectName.includes(groundStationName)) {
    //           self.displayGroundStationTimeline(groundStationName)
    //         }
    //       }
    //     } // else Globe was not picked
    //   }, Cesium.ScreenSpaceEventType.MIDDLE_CLICK)
    // },

    cleanViews() {
      this.cleanMapView()
      this.cleanHeatmapView()
      this.cleanTimelines()
      this.cleanHistogramView()
      this.cleanGlobalHistogramView()
      return
    },

    prepareAndDisplayData() {
      this.buildingMap = true

      // When switching page, purge plotly context to avoid memory leaks and errors when resizing page
      // No catch if the plot didn't rendered
      this.cleanViews()

      if (this.dataLoaded) {
        // prepare data
        this.updateDefaultMapFromCategory(this.appliedDashboardConfiguration)
        this.updateDefaultMapFromCategory(this.editedDashboardConfiguration)
        if (this.appliedDashboardConfiguration.currentMap == null) {
          this.buildingMap = false
          return
        }
        this.generateData()
        for (const data of this.currentDataRef) {
          if (data.z.length === 0) {
            this.buildingMap = false
            this.emptyResult = true
            return
          }
        }
        for (const data of this.currentDataOther) {
          if (data.z.length === 0) {
            this.buildingMap = false
            this.emptyResult = true
            return
          }
        }
        this.generateGradient()
        // display containers
        if (this.emptyResult === false) {
          // if (this.appliedDashboardConfiguration.displayMapView === true) {
          //   this.drawGeo()
          // }
          if (this.appliedDashboardConfiguration.displayHeatmapView === true) {
            this.drawHeatMap()
          }
          if (
            this.appliedDashboardConfiguration.displayHistogramView === true
          ) {
            this.drawHistogram()
          }
          if (
            this.appliedDashboardConfiguration.displayGlobalHistogramView ===
            true
          ) {
            this.drawGlobalHistogram()
          }
        }
      }

      this.buildingMap = false
    },

    generatePlotlyColorScale() {
      let gradient = this.heatMapCommons.currentGradient
      return gradient.map((val, i) => {
        return [
          constellationUtils.scale(i.toString(), 0, gradient.length - 1, 0, 1),
          val
        ]
      })
    },
    generatePlotlyHeatmapTicks() {
      let gradient = this.heatMapCommons.currentGradient
      this.heatMapCommons.gradientStep = 20
      this.heatMapCommons.tickValues = []
      this.heatMapCommons.tickText = []
      for (
        let i = 0;
        i < gradient.length / this.heatMapCommons.gradientStep;
        i++
      ) {
        let approximatedValue = constellationUtils.scale(
          i * this.heatMapCommons.gradientStep,
          0,
          gradient.length - 1,
          this.heatMapCommons.zMin,
          this.heatMapCommons.zMax
        )
        this.heatMapCommons.tickText.push(
          this.valueToUnitText(approximatedValue)
        )
        this.heatMapCommons.tickValues.push(approximatedValue.toFixed(1))
      }
    },
    drawHeatMap() {
      this.drawHeatMapFromData(
        this.currentDataRef,
        this.idWidgetHeatmapViewRef,
        this.heatmapMeshIndexCurrent
      )
      this.drawHeatMapFromData(
        this.currentDataOther,
        this.idWidgetHeatmapViewOther,
        this.heatmapMeshIndexOther
      )
    },

    drawHeatMapFromData(dataset, heatmapView, heatmapMeshIndex) {
      if (!dataset[heatmapMeshIndex]) {
        return
      }
      let xValues = dataset[heatmapMeshIndex].x
      let yValues = dataset[heatmapMeshIndex].y
      let zValues = dataset[heatmapMeshIndex].z
      const text = zValues.map((row, i) =>
        row.map((item, j) => {
          return `Longitude: ${dataTools.formatAngle(xValues[j])}<br>
        Latitude: ${dataTools.formatAngle(yValues[i])}<br>
        ${this.getResultsFieldLabel(
          this.appliedDashboardConfiguration.currentMap
        )}: ${this.valueToUnitText(item)}`
        })
      )
      let data = [
        {
          x: xValues,
          y: yValues,
          z: zValues,
          type: 'heatmap',
          showscale: true,
          colorscale: this.heatMapCommons.colorscale,
          zmin: this.heatMapCommons.zMin,
          zmax: this.heatMapCommons.zMax,
          text: text,
          hoverinfo: 'text',
          zsmooth: 'best',
          connectgaps: false,
          colorbar: {
            tickmode: 'array',
            tickvals: this.heatMapCommons.tickValues,
            ticktext: this.heatMapCommons.tickText,
            tickfont: {
              color: this.getPlotTextColor
            }
          }
        }
      ]
      let layout = {
        xaxis: {
          title: {
            text: 'Longitude (deg)',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          title: {
            text: 'Latitude (deg)',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }
      try {
        Plotly.react(heatmapView, data, layout, { responsive: true })
      } catch (_) {}
    },

    drawHistogram() {
      this.drawHistogramFromData(
        this.currentDataRef,
        this.idWidgetHistogramViewRef,
        this.statisticsHistogramMeshIndexCurrent
      )
      this.drawHistogramFromData(
        this.currentDataOther,
        this.idWidgetHistogramViewOther,
        this.statisticsHistogramMeshIndexOther
      )
    },

    drawHistogramFromData(
      dataset,
      histogramView,
      statisticsHistogramMeshIndex
    ) {
      let data = []
      let xData =
        statisticsHistogramMeshIndex === this.allMeshesIndex
          ? this.zNotNull(dataset)
          : this.zNotNull([dataset[statisticsHistogramMeshIndex]])
      let dataRange =
        Math.ceil(Math.max(...xData)) - Math.floor(Math.min(...xData))
      let lastHistogramOptionUpdated
      let xbinsSize

      if (histogramView == this.idWidgetHistogramViewRef) {
        lastHistogramOptionUpdated = this.lastHistogramOptionUpdatedRef
      } else {
        lastHistogramOptionUpdated = this.lastHistogramOptionUpdatedOther
      }

      if (lastHistogramOptionUpdated == 'Bin size') {
        xbinsSize =
          histogramView == this.idWidgetHistogramViewRef
            ? this.appliedDashboardConfiguration.histogramBinSizeRef
            : this.appliedDashboardConfiguration.histogramBinSizeOther
      } else {
        xbinsSize =
          histogramView == this.idWidgetHistogramViewRef
            ? dataRange /
              Math.ceil(
                this.appliedDashboardConfiguration.histogramNumberOfBinsRef
              )
            : dataRange /
              Math.ceil(
                this.appliedDashboardConfiguration.histogramNumberOfBinsOther
              )
      }
      if (isNaN(Number(xbinsSize)) || xbinsSize == Infinity) {
        xbinsSize = ''
      }

      if (this.appliedDashboardConfiguration.displayOccurenceHistogram) {
        data.push({
          type: 'histogram',
          name: 'Occurence',
          histnorm: 'percent',
          xbins: {
            size: xbinsSize
          },
          x: xData,
          hovertemplate: '%{y}<extra></extra>'
        })
      }
      if (this.appliedDashboardConfiguration.displayCumulativeHistogram) {
        data.push({
          type: 'histogram',
          name: 'Cumulative',
          histnorm: 'percent',
          cumulative: { enabled: true },
          xbins: {
            size: xbinsSize
          },
          x: xData,
          xaxis: 'x2',
          yaxis: 'y2',
          hovertemplate: '%{y}<extra></extra>'
        })
      }

      let xAxisText
      if (
        this.appliedDashboardConfiguration.currentCategory ==
        'pointNumberOfAccesses'
      ) {
        xAxisText = this.getResultsFieldLabel(
          this.appliedDashboardConfiguration.currentCategory
        )
      } else {
        xAxisText =
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentMap
          ) +
          ' ' +
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentCategory
          ).toLowerCase()
      }

      let nGraphs = data.length
      let layout = {
        grid: { rows: nGraphs, columns: 1, pattern: 'independent' },
        title: {
          text: 'Percentage (%) of mesh points',
          font: {
            color: this.getPlotTextColor,
            size: 20
          }
        },
        legend: {
          font: {
            color: this.getPlotTextColor
          }
        },
        xaxis: {
          title: {
            text:
              this.appliedDashboardConfiguration.displayOccurenceHistogram &&
              this.appliedDashboardConfiguration.displayCumulativeHistogram
                ? ''
                : xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          },
          range: [
            this.zDataFilteredAbsoluteMin(),
            this.zDataFilteredAbsoluteMax()
          ]
        },
        xaxis2: {
          title: {
            text: xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          },
          range: [
            this.zDataFilteredAbsoluteMin(),
            this.zDataFilteredAbsoluteMax()
          ]
        },
        yaxis: {
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis2: {
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }

      let self = this
      let config = {
        responsive: true,
        modeBarButtonsToAdd: [
          {
            name: 'Toggle bin configurations',
            icon: Plotly.Icons['pencil'],
            click: function () {
              self.displayHistogramOptions = !self.displayHistogramOptions
            }
          }
        ],
        modeBarButtonsToRemove: ['hoverClosestCartesian']
      }

      try {
        Plotly.react(histogramView, data, layout, config)

        let plot = document.getElementById(histogramView)
        this.correctPercentageToTotalNumberOfMeshPoints(histogramView)
        this.correctHoverUnits(plot)
        this.correctHoverPercentageToTotalNumberOfMeshPoints(
          plot,
          histogramView
        )
        this.correctXAxisTextUnits(plot)
        Plotly.restyle(histogramView, {})
        Plotly.relayout(histogramView, {})
      } catch (_) {}
    },

    correctPercentageToTotalNumberOfMeshPoints(histogramView) {
      let totalNumberOfPoints
      let numberOfPointsNotNull
      if (histogramView == this.idWidgetHistogramViewRef) {
        totalNumberOfPoints =
          this.currentResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ].length *
          this.currentResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ][0].length
        const xData =
          this.statisticsHistogramMeshIndexCurrent === this.allMeshesIndex
            ? this.zNotNull(this.currentDataRef)
            : this.zNotNull([
                this.currentDataRef[this.statisticsHistogramMeshIndexCurrent]
              ])
        numberOfPointsNotNull = xData.length
      } else {
        totalNumberOfPoints =
          this.otherResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ].length *
          this.otherResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ][0].length
        const xData =
          this.statisticsHistogramMeshIndexOther === this.allMeshesIndex
            ? this.zNotNull(this.currentDataOther)
            : this.zNotNull([
                this.currentDataOther[this.statisticsHistogramMeshIndexOther]
              ])
        numberOfPointsNotNull = xData.length
      }

      Plotly.d3.selectAll('.ytick text').each(function (d, i) {
        Plotly.d3
          .select(this)
          .html(
            (Plotly.d3.select(this).html() * numberOfPointsNotNull) /
              totalNumberOfPoints
          )
      })
      Plotly.d3.selectAll('.y2tick text').each(function (d, i) {
        Plotly.d3
          .select(this)
          .html(
            (Plotly.d3.select(this).html() * numberOfPointsNotNull) /
              totalNumberOfPoints
          )
      })
    },

    correctHoverPercentageToTotalNumberOfMeshPoints(plotId, histogramView) {
      let totalNumberOfPoints
      let numberOfPointsNotNull
      if (histogramView == this.idWidgetHistogramViewRef) {
        totalNumberOfPoints =
          this.currentResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ].length *
          this.currentResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ][0].length
        const xData =
          this.statisticsHistogramMeshIndexCurrent === this.allMeshesIndex
            ? this.zNotNull(this.currentDataRef)
            : this.zNotNull([
                this.currentDataRef[this.statisticsHistogramMeshIndexCurrent]
              ])
        numberOfPointsNotNull = xData.length
      } else {
        totalNumberOfPoints =
          this.otherResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ].length *
          this.otherResultsLog.results[
            this.appliedDashboardConfiguration.currentMap
          ][0].length
        const xData =
          this.statisticsHistogramMeshIndexOther === this.allMeshesIndex
            ? this.zNotNull(this.currentDataOther)
            : this.zNotNull([
                this.currentDataOther[this.statisticsHistogramMeshIndexOther]
              ])
        numberOfPointsNotNull = xData.length
      }

      plotId.on('plotly_hover', function (data) {
        let old_hoverText = Plotly.d3.selectAll('.hovertext text.nums')
        let old_hoverPath = Plotly.d3.selectAll('.hovertext').selectAll('path')
        // if heatmap hover is currently being shown, we don't wanna interfere with it
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }
        // twice to consider if both the map and heatmap hovers are active
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }
        //twice to consider the hover from both missions
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }
        //twice to consider the hover from both missions
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }

        let new_hoverText =
          ' ' +
          (old_hoverText.html() * numberOfPointsNotNull) / totalNumberOfPoints

        //duplicate the hover text
        var attr = old_hoverText.node().attributes
        var attr_length = attr.length
        var node_name = old_hoverText.property('nodeName')
        var parent = Plotly.d3.select(old_hoverText.node().parentNode)
        var cloned = parent.append(node_name).attr('id', 'new_hovertext')
        var j = 0
        for (j = 0; j < attr_length; j += 1) {
          if (attr[j].nodeName !== 'id') {
            cloned.attr(attr[j].name, attr[j].value)
          }
        }
        let oldTextLength = old_hoverText[0][0].textContent.length
        let newTextLength = new_hoverText.length

        old_hoverPath
          .node()
          .setAttribute(
            'transform',
            'scale(' + newTextLength / oldTextLength + ',1)'
          )

        //assign the correct text to the next x-axis info text
        cloned.text(new_hoverText)
        cloned.style('opacity', 1)
        old_hoverText.style('opacity', 0)
      })
    },

    correctHoverUnits(plotId) {
      let self = this
      plotId.on('plotly_hover', function (data) {
        var old_infotext = Plotly.d3.selectAll('.axistext').selectAll('text')
        let valuesInRange = old_infotext[0][0].textContent.split('-')
        let new_infotext = self.valueToUnitText(
          valuesInRange[0].replace('−', '-')
        ) //plotly writes an 'em dash' for negative values, which is different from the minus sign
        if (valuesInRange.length == 2) {
          new_infotext +=
            ' - ' + self.valueToUnitText(valuesInRange[1].replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
        }

        //duplicate the x-axis info text
        var attr = old_infotext.node().attributes
        var attr_length = attr.length
        var node_name = old_infotext.property('nodeName')
        var parent = Plotly.d3.select(old_infotext.node().parentNode)
        var cloned = parent.append(node_name).attr('id', 'new_axistext')
        var j = 0
        for (j = 0; j < attr_length; j += 1) {
          if (attr[j].nodeName !== 'id') {
            cloned.attr(attr[j].name, attr[j].value)
          }
        }

        let oldTextLength = old_infotext[0][0].textContent.length
        let newTextLength = new_infotext.length

        Plotly.d3
          .selectAll('.axistext')
          .selectAll('path')
          .node()
          .setAttribute(
            'transform',
            'scale(' + newTextLength / oldTextLength + ',1)'
          )

        //assign the correct text to the next x-axis info text
        cloned.text(new_infotext)
        cloned.style('opacity', 1)
        old_infotext.style('opacity', 0)
      })
    },

    correctXAxisTextUnits(plotId) {
      let self = this
      plotId.on('plotly_restyle', function () {
        Plotly.d3.selectAll('.xtick text').each(function (d, i) {
          Plotly.d3
            .select(this)
            .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
          if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
            Plotly.d3
              .select(this)
              .html(self.valueToUnitText(Plotly.d3.select(this).html()))
          }
        })
        Plotly.d3.selectAll('.x2tick text').each(function (d, i) {
          Plotly.d3
            .select(this)
            .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
          if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
            Plotly.d3
              .select(this)
              .html(self.valueToUnitText(Plotly.d3.select(this).html()))
          }
        })
      })
      plotId.on('plotly_relayout', function () {
        Plotly.d3.selectAll('.xtick text').each(function (d, i) {
          Plotly.d3
            .select(this)
            .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
          if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
            Plotly.d3
              .select(this)
              .html(self.valueToUnitText(Plotly.d3.select(this).html()))
          }
        })
        Plotly.d3.selectAll('.x2tick text').each(function (d, i) {
          Plotly.d3
            .select(this)
            .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
          if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
            Plotly.d3
              .select(this)
              .html(self.valueToUnitText(Plotly.d3.select(this).html()))
          }
        })
      })
    },

    drawGlobalHistogram() {
      let histogramDataName =
        this.appliedDashboardConfiguration.currentCategory + 'GlobalHistogram'
      let histogramDataRef = this.generateHistogramDataFromResults(
        this.currentResultsLog.results[histogramDataName]
      )
      let histogramDataOther = this.generateHistogramDataFromResults(
        this.otherResultsLog.results[histogramDataName]
      )

      this.drawGlobalHistogramFromData(
        histogramDataRef,
        this.idWidgetGlobalHistogramViewRef
      )
      this.drawGlobalHistogramFromData(
        histogramDataOther,
        this.idWidgetGlobalHistogramViewOther
      )
    },

    drawGlobalHistogramFromData(dataset, globalHistogramView) {
      // let histogramDataName = this.appliedDashboardConfiguration.currentCategory+'GlobalHistogram'
      // let histogramData = this.generateHistogramDataFromResults(this.currentResultsLog.results[histogramDataName])

      let data = []
      if (this.appliedDashboardConfiguration.displayOccurenceHistogram) {
        data.push({
          type: 'bar',
          name: 'Occurence',
          x: dataset.binMiddle,
          y: dataset.binValue
        })
      }
      if (this.appliedDashboardConfiguration.displayCumulativeHistogram) {
        data.push({
          type: 'bar',
          name: 'Cumulative',
          x: dataset.binMiddle,
          y: dataset.binValueCumulative,
          xaxis: 'x2',
          yaxis: 'y2'
        })
      }

      let nGraphs = data.length
      let xAxisText = this.getResultsFieldLabel(
        this.appliedDashboardConfiguration.currentCategory
      )
      let layout = {
        bargap: 0.0,
        grid: { rows: nGraphs, columns: 1, pattern: 'independent' },
        title: {
          text:
            'Global ' +
            this.getResultsFieldLabel(
              this.appliedDashboardConfiguration.currentCategory
            ).toLowerCase() +
            ' histogram',
          font: {
            color: this.getPlotTextColor
          }
        },
        legend: {
          font: {
            color: this.getPlotTextColor
          }
        },
        xaxis: {
          title: {
            text:
              this.appliedDashboardConfiguration.displayOccurenceHistogram &&
              this.appliedDashboardConfiguration.displayCumulativeHistogram
                ? ''
                : xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        xaxis2: {
          title: {
            text: xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          title: {
            text: '% of total incidences',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis2: {
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }
      try {
        Plotly.react(globalHistogramView, data, layout, { responsive: true })
        let plot = document.getElementById(globalHistogramView)
        this.correctXAxisTextUnits(plot)
        this.correctHoverUnits(plot)
        Plotly.restyle(globalHistogramView, {})
        Plotly.relayout(globalHistogramView, {})
      } catch (_) {}
    },

    drawGeo() {
      this.drawGeoFromData(this.currentDataRef, this.idWidgetMapViewRef)
      this.drawGeoFromData(this.currentDataOther, this.idWidgetMapViewOther)
    },

    drawGeoFromData(dataset, mapView) {
      let xValues = dataset.x
      let yValues = dataset.y
      let zValues = dataset.z
      let x = []
      let y = []
      let z = []
      let i = 0
      const text = []
      xValues.forEach((lon, lonIndex) => {
        yValues.forEach((lat, latIndex) => {
          let value = zValues[latIndex][lonIndex]
          if (
            value !== null &&
            (this.currentMission.earthMesh.minLongitude !== -Math.PI ||
              this.currentMission.earthMesh.maxLongitude !== Math.PI ||
              lonIndex !== xValues.length - 1)
          ) {
            x[i] = lon
            y[i] = lat
            z[i] = value
            text[i] = `Longitude: ${dataTools.formatAngle(x[i])}<br>
Latitude: ${dataTools.formatAngle(y[i])}<br>
${this.getResultsFieldLabel(
  this.appliedDashboardConfiguration.currentMap
)}: ${this.valueToUnitText(z[i])}
<extra></extra>`
            i++
          }
        })
      })

      let data = [
        {
          type: 'scattermapbox',
          mode: 'markers',
          text: z,
          lon: x,
          lat: y.map((el) => (isNaN(el) ? 0 : el)),
          hovertemplate: text,
          hoverinfo: 'text',
          showlegend: false,
          marker: {
            color: z,
            colorscale: this.heatMapCommons.colorscale,
            cmin: this.heatMapCommons.zMin,
            cmax: this.heatMapCommons.zMax,
            reversescale: false,
            opacity: 0.5,
            colorbar: {
              tickmode: 'array',
              tickvals: this.heatMapCommons.tickValues,
              ticktext: this.heatMapCommons.tickText,
              tickfont: {
                color: this.getPlotTextColor
              }
            }
          }
        }
      ]

      let layout = {
        dragmode: 'zoom',
        mapbox: {
          center: {
            lat: (Math.min(...yValues) + Math.max(...yValues)) / 2,
            lon: (Math.min(...xValues) + Math.max(...xValues)) / 2
          },
          domain: {
            x: [0, 1],
            y: [0, 1]
          },
          style: this.$vuetify.theme.dark ? 'dark' : 'light',
          zoom: 1
        },

        margin: {
          r: 0,
          t: 0,
          b: 0,
          l: 0,
          pad: 0
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor,
        showlegend: false
      }
      Plotly.setPlotConfig({
        mapboxAccessToken: this.mapboxToken
      })
      try {
        Plotly.react(mapView, data, layout, { responsive: true })
      } catch (e) {
        // no message
      }
    },
    getResultFieldType(field) {
      if (
        resultsDescription.ConstellationPerformanceAnalysisResult[field] == null
      ) {
        console.log(
          'no field ' + field + ' in results description, type not found'
        )
        return ''
      }
      return resultsDescription.ConstellationPerformanceAnalysisResult[field]
        .type
    },
    getResultsFieldLabel(field) {
      if (
        resultsDescription.ConstellationPerformanceAnalysisResult[field] == null
      ) {
        console.log(
          'no field ' + field + ' in results description, label not found'
        )
        return ''
      }
      return resultsDescription.ConstellationPerformanceAnalysisResult[field]
        .label
    },
    getMeshName(index, results) {
      return results?.earthMeshes[index]?.name
        ? results?.earthMeshes[index]?.name
        : index
    },
    computeRange(fieldType, fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog === null ||
          this.currentResultsLog.results == null ||
          this.currentResultsLog.results.ephemerides != null
        ) {
          return ''
        } else {
          currentResults = this.currentResultsLog.results
        }
      }
      let allVals = dataTools
        .getUniqueValues(currentResults, fieldPath)
        .filter((value) => !isNaN(value) && value != null)
      let min = dataTools.convertSi2Display(fieldType, Math.min(...allVals))
      let max = dataTools.convertSi2Display(fieldType, Math.max(...allVals))
      if (min === max) {
        return min.toString()
      } else {
        if (min.toString().includes('Infinity')) {
          return ''
        }
        return '[' + min + ' ... ' + max + ']'
      }
    },
    // TODO copied from analytical mission, it would be better to reuse the code

    isConstantLatitudeStep(earthMesh) {
      const lats = earthMesh.latitudes
      if (lats.length > 1) {
        return (
          Math.sin(lats[lats.length - 1]) - Math.sin(lats[lats.length - 2]) ==
          Math.sin(lats[1]) - Math.sin(lats[0])
        )
      } else {
        return true
      }
    },

    getStatisticalMetricName(currentMap) {
      if (currentMap.includes('forwardLinkTransferTime')) {
        return 'FORWARD_LINK_TRANSFER_TIME'
      }
      if (currentMap.includes('reverseLinkTransferTime')) {
        return 'REVERSE_LINK_TRANSFER_TIME'
      }
      if (currentMap.includes('timeBetweenReverseLinkArrivals')) {
        return 'TIME_BETWEEN_REVERSE_LINK_ARRIVALS'
      }
      if (currentMap.includes('pointNumberOfAccesses')) {
        return ''
      }
      if (currentMap.includes('pointAccessDuration')) {
        return 'ACCESS_DURATION'
      }
      if (currentMap.includes('pointRevisitTime')) {
        return 'REVISIT_TIME'
      }
      if (currentMap.includes('forwardToReverseLinkTime')) {
        return 'FORWARD_TO_REVERSE_LINK_TIME'
      }
      if (currentMap.includes('pointCollectTime')) {
        return 'COLLECT_TIME'
      }
      if (currentMap.includes('pointSimultaneousSpacecrafts')) {
        return 'SPACECRAFTS_SIMULTANEOUSLY_SEEN'
      }
    },

    getStatisticalMetricStatistic(currentMap) {
      if (currentMap.includes('Average')) {
        return 'average'
      }
      if (currentMap.includes('Median')) {
        return 'median'
      }
      if (currentMap.includes('Max')) {
        return 'maximum'
      }
      if (currentMap.includes('Min')) {
        return 'minimum'
      }
      if (currentMap.includes('Percentile')) {
        return 'percentiles'
      }
      if (currentMap.includes('Cumulative')) {
        return 'cumulative'
      }
    },

    // -------------------------------------------------------------------
    mergeString(fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog.results == null
        ) {
          return ''
        } else {
          currentResults = this.currentResultsLog.results
        }
      }
      let uniqueVals = dataTools.getUniqueValues(currentResults, fieldPath)
      if (uniqueVals.length === 1) {
        return uniqueVals[0]
      } else {
        return uniqueVals
      }
    },

    isStringInside(expected, fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog.results == null
        ) {
          return ''
        } else {
          currentResults = this.currentMission.results
        }
      }
      return (
        currentResults.filter(
          (element) => dataTools.getValue(element, fieldPath) === expected
        ).length > 0
      ) // TODO stop on first would be better
    }
  }
}
</script>

<style scoped>
.cesiumColorScale {
  position: absolute;
  left: 1%;
  top: 1%;
  z-index: 2;
  color: #fff;
}
.bug-button {
  background: #ff5252 !important;
}
</style>
